import Vue from 'vue';
import Vuex from 'vuex';

import { getOptimizeEnrichments, getOptimizeManifest } from '../api';
import { enrichEnrichmentModel, enrichIntentModel } from '../utils';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    intents: [],
    initialSelection: {},
    optimizeConfig: {
      optimizeApiKey: '',
      optimizeApiHost: '',
      optimizeProjectId: '',
    },
    error: '',
    debug: process.env.VUE_APP_DEBUG === 'true',
    loading: true,
  },
  mutations: {
    setUniformIntents(state, payload) {
      state.intents = enrichIntentModel(payload);

      if (!state.initialSelection || !state.initialSelection.intents) {
        return false;
      }

      state.intents.forEach((intent) => {
        const givenIntent = state.initialSelection.intents[intent.id];
        if (givenIntent) {
          intent.selected = true;
          intent.noBeh = givenIntent.noBeh;
          intent.noPn = givenIntent.noPn;
          intent.override = givenIntent.override;
          intent.threshold = givenIntent.threshold;

          if (givenIntent.str) {
            intent.signals[0].str = givenIntent.str;
          }
        }
      });
    },

    setUniformEnrichments(state, payload) {
      const enrichments = enrichEnrichmentModel(payload);

      enrichments.forEach((enrichment) => {
        state.intents.push(enrichment);
      });

      if (!state.initialSelection || !state.initialSelection.intents) {
        return false;
      }

      state.intents.forEach((intent) => {
        const givenIntent = state.initialSelection.intents[intent.id];
        if (givenIntent) {
          intent.selected = true;
          intent.noPn = givenIntent.noPn;
          intent.override = givenIntent.override;
          intent.threshold = givenIntent.threshold;
        }
      });
    },

    setOptimizeConfig(state, payload) {
      state.optimizeConfig = payload;
    },

    addIntent(state, payload) {
      state.intents.find((intent) => intent.id === payload).selected = true;
    },

    removeIntent(state, payload) {
      const selectedIntent = state.intents.find((intent) => intent.id === payload);
      if (selectedIntent) {
        selectedIntent.selected = false;
        selectedIntent.advancedOpen = false;
      }
    },

    showAdvancedFeatures(state, payload) {
      state.intents.find((intent) => intent.id === payload).advancedOpen = true;
    },

    hideAdvancedFeatures(state, payload) {
      state.intents.find((intent) => intent.id === payload).advancedOpen = false;
    },

    setInitialSelection(state, payload) {
      state.initialSelection = payload;
    },

    saveAdvancedOptions(state, payload) {
      for (const item in payload.data) {
        if (item === 'str') {
          state.intents.find((intent) => intent.id === payload.intentId).signals[0].str = payload.data.str;
        } else {
          state.intents.find((intent) => intent.id === payload.intentId)[item] = payload.data[item];
        }
      }
    },

    setError(state, payload) {
      state.error = payload;
    },

    setLoading(state, payload) {
      state.loading = payload;
    },
  },
  getters: {
    getDebug(state) {
      return state.debug;
    },

    getDropdownIntents(state) {
      return state.intents.filter((intent) => !intent.selected && intent.type === 'intent');
    },

    getSelectedIntents(state) {
      return state.intents.filter((intent) => intent.selected && intent.type === 'intent');
    },

    getDropdownEnrichments(state) {
      return state.intents.filter((intent) => !intent.selected && intent.type === 'enrichment');
    },

    getSelectedEnrichments(state) {
      return state.intents.filter((intent) => intent.selected && intent.type === 'enrichment');
    },

    getIntentsToSave(state) {
      const result = { intents: {} };

      state.intents
        .filter((intent) => intent.selected)
        .forEach((intent) => {
          result.intents[intent.id] = {
            str: intent.signals[0].str,
          };

          if (intent.noBeh) {
            result.intents[intent.id].noBeh = true;
          }

          if (intent.noPn) {
            result.intents[intent.id].noPn = true;
          }

          if (intent.override) {
            result.intents[intent.id].override = intent.override;
          }

          if (intent.threshold) {
            result.intents[intent.id].threshold = intent.threshold;
          }
        });

      return result;
    },

    getError(state) {
      return state.error;
    },

    hasEnrichments(state) {
      return state.intents.filter((intent) => intent.type === 'enrichment').length > 0;
    },

    getLoading(state) {
      return state.loading;
    },

    hasIntents(state) {
      return state.intents.length > 0;
    },
  },
  actions: {
    async getDataFromUniform({ commit, state }) {
      const siteData = await getOptimizeManifest(state.optimizeConfig);

      if (siteData.error === null) {
        commit('setError', null);
        commit('setUniformIntents', siteData.result);
      } else {
        commit('setError', siteData.error);
      }

      const enrichmentData = await getOptimizeEnrichments(state.optimizeConfig);

      if (enrichmentData.error === null) {
        commit('setError', null);
        commit('setUniformEnrichments', enrichmentData.result);
      } else {
        commit('setError', enrichmentData.error);
      }

      commit('setLoading', false);
    },
  },
});
